import { Container, Col, Row, BreadcrumbItem, Badge, Input, Alert } from "reactstrap";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import ContentTabs from "@repoflow/components/content-tabs";
import { Form as ValuesForm } from "@repoflow/components/types-values";
import * as Notify from "@repoflow/form-schema/notify";
import * as TableQL from "queries/table";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import { Component as Form } from "@repoflow/form-schema";
import * as appWidgets from "@shared/widgets/register";

export const Sidebar = () => null;

export const Indice = () => (
  <>
    <BreadcrumbItem>Edit</BreadcrumbItem>
  </>
);

// A table's screen: its rows, its columns and its details.
//
// TWO SAVES, NOT ONE, AND THE SPLIT IS NOT COSMETIC.
//
// The columns and the details are properties of the DynamoDB row. The rows are a
// separate object in S3 with an ETag of its own, and that etag is what a write
// conditions on. They are two objects with two concurrency stories: somebody
// else can replace the rows — a sync will, once sources land — without touching
// the row, and a person renaming a column must not have that refused because of
// it, nor silently overwrite it.
//
// One Save would have to pick: either it sends the rows on every column rename
// (and loses whatever the other writer just did), or it does two conditional
// writes and has to explain a half-failure. Two Saves says the true thing.
export const Content = () => {
  const { values, base } = useSectionContext();

  // A SOURCED TABLE HAS NO COLUMNS TAB, and that is not a tidying decision.
  //
  // For a `system` table the column declaration IS the table — it lives on the
  // DynamoDB row and the row editor is compiled from it. For a sourced one the
  // columns are inferred from what the source sent and live on the rows object
  // in S3, so the row's `columns` is `[]` and always will be. Offering a
  // Columns tab there would offer an editor whose every save is a no-op that
  // the next sync silently confirms.
  const sourced = (values.sourceType || "system") !== "system";

  const tabs = [
    { label: "Rows", content: <RowsTab sourced={sourced} /> },
    ...(sourced ? [] : [{ label: "Columns", content: <TableForm only={"columns"} /> }]),
    { label: "Source", content: <SourceTab /> },
    { label: "Details", content: <TableForm only={"details"} /> },
  ];

  return (
    <>
      <Container fluid className="mt-4">
        <Row>
          <Col>
            <div className="float-right">
              <Link className="btn btn-danger" to={`${base}/remove`}>
                Remove
              </Link>
            </div>
            <h4 className="mb-0">
              {values.title}{" "}
              <Badge color="light" className="border align-middle">
                {values.tableId}
              </Badge>
            </h4>
            <small className="text-muted">
              {(values.rowCount || 0).toLocaleString()} row
              {values.rowCount === 1 ? "" : "s"}
            </small>
          </Col>
        </Row>
      </Container>

      <div className="mt-3" />

      <ContentTabs tabs={tabs} />
    </>
  );
};

// The columns and the details, over /Public/Table/Update. One mutation, shown
// as two tabs because they are two jobs — but they are one row, so they share a
// Save and neither can be left behind by the other.
const TableForm = ({ only }) => {
  const { values, attributes } = useSectionContext();

  // Every field the mutation sends, whichever tab is showing. /Public/Table/Update
  // requires `title`, and the model's `keep` helper treats absent as "leave it" —
  // so a Source tab that sent only its own two fields would still have to carry
  // the title, and carrying all of them is both simpler and what makes the three
  // tabs one row rather than three.
  const initial = {
    title: values.title || "",
    description: values.description || "",
    columns: values.columns || [],
    tags: values.tags || [],
    sourceType: values.sourceType || "system",
    source: values.source || {},
  };

  const fields =
    only === "columns"
      ? [{ property: "columns", layout: "wide" }]
      : only === "source"
        // `sourceType` FIRST, because it is the resolver: form-schema picks the
        // branch of the `source` oneOf from its value, so the field that
        // decides has to be the field above the one it decides.
        ? [{ property: "sourceType" }, { property: "source", layout: "wide" }]
        : [{ property: "title" }, { property: "description" }, { property: "tags" }];

  return (
    <Mutation
      initial={initial}
      target={{ $ref: "/Public/Table/Update" }}
      mutation={TableQL.edit}
      update={TableQL.update(attributes)}
      context={{ fields }}
      onMutate={({ state: { values: v }, handler }) => {
        handler({ variables: { tableId: attributes.tableId, input: v } });
      }}
      onCompleted={() => Notify.success("Saved")}
      onError={(error) => Notify.error(error.toString())}
    >
      {TableFormHandler}
    </Mutation>
  );
};

// The Mutation hands its children the section context — `schema`, `state`,
// `mutate`, `target` — and NOT a Fields component. The form is built from those,
// which is the shape every other screen in this app uses.
const TableFormHandler = ({ schema, state, mutate, target, context: { fields } }) => {
  const { values, setValues } = state;

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Form
            schema={schema}
            target={target}
            values={values}
            widgets={appWidgets}
            onSubmit={() => mutate()}
            onChange={(vals) => setValues(vals)}
            config={{ "/Public/Table/Update": { fields } }}
          >
            <Button type="submit">Save</Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

// The source: where the rows come from, the credential that reaches it, and the
// button that pulls.
//
// Three separate writes on one screen, deliberately — they are three different
// things with three different lifetimes. The coordinates are an ordinary field
// on the row and are saved with Save. The credential goes ONE WAY through its
// own mutation and never comes back. The sync is not a write at all: it claims a
// job and returns, and the screen watches the row until the job says it is done.
const SourceTab = () => {
  const { values } = useSectionContext();

  const sourceType = values.sourceType || "system";

  if (sourceType === "system") {
    return (
      <Container fluid className="mt-4">
        <Row>
          <Col>
            <TableForm only={"source"} />
            <p className="text-muted mt-3 mb-0">
              This table is filled in by hand. Pick a source above and save to
              pull its rows from Airtable, Google Sheets or a CSV instead — the
              columns are then inferred from what the source sends, and the rows
              here are replaced on every sync.
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <TableForm only={"source"} />
      {sourceType === "airtable" ? <CredentialForm /> : null}
      {sourceType === "gsheet" ? <SheetsAccessNote /> : null}
      <SyncPanel />
    </>
  );
};

// Google Sheets uses ONE platform OAuth client, not a per-tenant one, so a sheet
// is reachable only if it has been shared with that account. Said on the screen
// because there is no way to discover it from a failure: Google answers a sheet
// it cannot see and a sheet that does not exist identically.
const SheetsAccessNote = () => (
  <Container fluid className="mt-3">
    <Row>
      <Col>
        <Alert color="info" className="mb-0">
          Share the spreadsheet with this service's Google account before
          syncing — read access is enough. A sheet that has not been shared
          reads exactly like one that does not exist.
        </Alert>
      </Col>
    </Row>
  </Container>
);

// The credential, one way.
//
// Plain local state and a plain mutation rather than a form over the schema,
// because the credential is NOT a schema property — that is the whole point of
// where it is stored. What comes back is the table, carrying `credentialHint`;
// the secret itself has no field on any type and no query can ask for it.
const CredentialForm = () => {
  const { values, attributes, refetch } = useSectionContext();
  const [secret, setSecret] = useState("");

  const hint = values.credentialHint || "";

  return (
    <Mutation
      mutation={TableQL.setCredential}
      onMutate={({ handler }) => {
        handler({ variables: { tableId: attributes.tableId, input: { secret } } });
      }}
      onCompleted={() => {
        Notify.success(secret ? "Access token saved" : "Access token cleared");
        // Cleared from the browser the moment the server has it. Leaving it in
        // an input is how a token ends up in a screenshot.
        setSecret("");
        refetch && refetch();
      }}
      onError={(error) => Notify.error(error.toString())}
    >
      {({ mutate }) => (
        <Container fluid className="mt-4">
          <Row>
            <Col>
              <h5>Access token</h5>
              <p className="text-muted mb-2">
                An Airtable personal access token with read access to the base.
                {hint ? ` One ending …${hint} is stored.` : " None is stored."}{" "}
                It is never shown again after saving.
              </p>
              <Input
                type="password"
                autoComplete="off"
                aria-label="Access token"
                placeholder={hint ? "Replace the stored token" : "pat…"}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
              <div className="mt-2">
                <Button onClick={() => mutate()}>
                  {secret ? "Save token" : "Clear token"}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </Mutation>
  );
};

const SYNC_POLL_MS = 5000;

// What the last sync did, and the button that starts the next one.
//
// NOTHING PUSHES. The mutation returns as soon as the claim is taken, and the
// pull happens in another Lambda that this browser has no channel to. So the
// screen polls the row while it says "processing" and stops the moment it does
// not — the archive did the same, at the same interval.
const SyncPanel = () => {
  const { values, attributes, refetch } = useSectionContext();

  const sync = values.sync || {};
  const processing = sync.status === "processing";

  useEffect(() => {
    if (!processing || !refetch) return undefined;
    const timer = setInterval(() => refetch(), SYNC_POLL_MS);
    // Cleared on the status change AND on unmount. A poll that outlives the
    // screen is the leak the archive's own engine had in a different guise.
    return () => clearInterval(timer);
  }, [processing, refetch]);

  return (
    <Mutation
      mutation={TableQL.sync}
      onMutate={({ handler }) => {
        handler({ variables: { tableId: attributes.tableId } });
      }}
      onCompleted={() => {
        Notify.success("Sync started");
        // Re-read once, immediately. The mutation returns the claimed table so
        // the cache should already say "processing" — but the poll below only
        // arms when it does, and one read costs nothing next to a screen that
        // silently never updates if it does not.
        refetch && refetch();
      }}
      onError={(error) => {
        const message = String(error);
        if (message.includes("table.sync.busy")) {
          Notify.error("A sync is already running for this table.");
          return;
        }
        if (message.includes("table.sync.unsupported")) {
          Notify.error("This table has no source to sync from.");
          return;
        }
        Notify.error(message);
      }}
    >
      {({ mutate }) => (
        <Container fluid className="mt-4">
          <Row>
            <Col>
              <h5>Sync</h5>
              <SyncStatus sync={sync} rowCount={values.rowCount || 0} />
              <div className="mt-2">
                <Button disabled={processing} onClick={() => mutate()}>
                  {processing ? "Syncing…" : "Sync now"}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </Mutation>
  );
};

// The named `sync.*` errors the handler and its adapters throw, as sentences.
//
// A person who mistyped a base id should read that, not `sync.config.incomplete`
// — and the ones that are NOT the tenant's fault must say so, or they will spend
// the afternoon re-checking a token that was never the problem.
const SYNC_ERRORS = {
  "sync.credential.missing": "No access token is stored for this table.",
  "sync.credential.rejected": "The stored access token was refused, or it cannot see that base.",
  "sync.source.rate-limited": "The source is rate-limiting us. Try again in a minute.",
  "sync.source.sheet-unreachable":
    "That spreadsheet cannot be read. Check the id, and that the sheet is shared with this service's Google account — an unshared sheet looks exactly like one that does not exist.",
  "sync.config.incomplete": "The source is missing something it needs — check the fields above.",
  "sync.source.host-not-public": "That URL resolves to a private address, so it cannot be fetched.",
  "sync.source.host-unresolvable": "That host does not resolve.",
  "sync.source.url-not-https": "Only https URLs can be fetched.",
  "sync.source.url-invalid": "That is not a URL.",
  "sync.source.redirect-invalid": "The server redirected without saying where to.",
  "sync.source.too-many-redirects": "The URL redirected too many times.",
  "sync.platform.google-credentials.missing":
    "Google Sheets is not configured on this deployment. This one is not yours to fix.",
};

const syncMessage = (error) => {
  if (!error) return "";
  if (SYNC_ERRORS[error]) return SYNC_ERRORS[error];

  // The parameterised ones, which carry the number that makes them useful.
  const [name, detail] = [error.split(":")[0], error.split(":")[1]];
  if (name === "sync.source.too-many-rows") {
    return `The source has ${Number(detail).toLocaleString()} rows, which is more than a table can hold.`;
  }
  if (name === "sync.source.too-large") {
    return "The source is larger than a table can hold.";
  }
  if (name === "sync.source.unreachable") {
    return detail === "timeout" ? "The source did not answer in time." : "The source could not be reached.";
  }
  if (name === "sync.source.http") {
    return `The source answered ${error.slice("sync.source.http-".length)}.`;
  }
  // Unmapped, so show it: a string nobody has written a sentence for is still
  // more use than "something went wrong".
  return error;
};

const SyncStatus = ({ sync, rowCount }) => {
  if (sync.status === "processing") {
    return (
      <p className="text-muted mb-0" role="status">
        Pulling rows… started {sync.startedAt ? new Date(sync.startedAt).toLocaleString() : "just now"}.
      </p>
    );
  }

  if (sync.status === "error") {
    return (
      <Alert color="danger" className="mb-0">
        <strong>The last sync failed.</strong>{" "}
        {syncMessage(sync.error)}{" "}
        {rowCount > 0
          ? "The rows from the previous sync are still here."
          : "This table has no rows yet."}
      </Alert>
    );
  }

  if (sync.status === "done") {
    return (
      <p className="text-muted mb-0">
        Last synced {sync.finishedAt ? new Date(sync.finishedAt).toLocaleString() : "recently"} —{" "}
        {(sync.rows || 0).toLocaleString()} row{sync.rows === 1 ? "" : "s"}.
      </p>
    );
  }

  return <p className="text-muted mb-0">This table has never been synced.</p>;
};

// The rows, compiled from the columns.
//
// This is the whole reason tablews is small: `types-values` takes a declaration
// and renders an ordinary form against it — same widgets, same validation, same
// dirty tracking — so a table's rows are edited by machinery pagews built for
// its sections and wfws reuses for its capture steps, and nothing here knows
// what a column is.
//
// The two-type shape is the archive's and is what makes a LIST of records out of
// a per-record declaration: `root` has one `list` param whose items are the
// `entry` type, and `entry` is the table's columns.
const RowsTab = () => {
  const { values, attributes, refetch } = useSectionContext();

  const declared = values?.rows?.columns?.length ? values.rows.columns : values.columns || [];

  // A column with no label falls back to its NAME.
  //
  // types-values compiles `label` straight through, so a column declared
  // without one renders an input with an empty <dt> — unaddressable to a screen
  // reader and unnameable to anything else. For a section param a blank label is
  // a deliberate choice; for a table column it is never right, because the name
  // IS the meaningful thing: it is the identifier the tenant will write in SQL.
  const columns = declared.map((column) => ({
    ...column,
    label: column.label || column.name,
  }));
  const [entries, setEntries] = useState(() => values?.rows?.entries || []);

  // The version these rows were read at. It goes back with the write, and a
  // mismatch is refused rather than overwriting — see data/table-rows.
  const etag = values?.rows?.etag || "";

  if (!columns.length) {
    return (
      <Container fluid className="mt-4">
        <Row>
          <Col className="text-muted">
            Declare some columns first — they are what a row is made of.
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Mutation
      mutation={TableQL.writeRows}
      update={TableQL.update(attributes)}
      onMutate={({ handler }) => {
        handler({
          variables: {
            tableId: attributes.tableId,
            input: { entries, columns, expectedEtag: etag },
          },
        });
      }}
      onCompleted={() => {
        Notify.success("Rows saved");
        // Re-read, because the etag has moved: the next save must condition on
        // the new one, and holding the old would make it conflict every time.
        refetch && refetch();
      }}
      onError={(error) => {
        const message = String(error);
        if (message.includes("table.rows.conflict")) {
          Notify.error(
            "These rows changed somewhere else since you opened them. Reload the table and reapply your edit."
          );
          return;
        }
        if (message.includes("table.rows.too-large")) {
          Notify.error(
            "That is more data than a table can hold in one go. Split it across two tables."
          );
          return;
        }
        Notify.error(message);
      }}
    >
      {({ mutate }) => (
        <Container fluid className="mt-4">
          <Row>
            <Col>
              <ValuesForm
                types={[
                  {
                    name: "root",
                    description: "",
                    params: [
                      {
                        name: "entries",
                        // Labelled rather than blank: it is the <dt> the list
                        // widget hangs its Add button off, so a blank one leaves
                        // the control unaddressable — to a screen reader and to
                        // anything else that finds a field by its name.
                        label: "Rows",
                        type: "list",
                        config: { type: "object", config: { type: "entry" } },
                        layout: "wide",
                      },
                    ],
                  },
                  { name: "entry", description: "", params: columns },
                ]}
                values={{ entries }}
                widgets={appWidgets}
                onChange={(v) => setEntries(v.entries || [])}
              />
              {/* No enclosing <Form> here — the rows are edited by
                  types-values, which owns its own state — so the button carries
                  the click rather than submitting. With no `target` on the
                  Mutation, section's Button is enabled: isEqual() is false and
                  isValid() is true, which is the same arrangement every remove
                  screen relies on. */}
              <Button onClick={() => mutate()}>Save rows</Button>
            </Col>
          </Row>
        </Container>
      )}
    </Mutation>
  );
};
